import React, {ReactNode, useEffect, useState} from "react";
import {Box, Button, ButtonBase, Grid, Skeleton, Stack, Tooltip, Typography} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import BeachAccessRoundedIcon from "@mui/icons-material/BeachAccessRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import ChairRoundedIcon from "@mui/icons-material/ChairRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import LightbulbRoundedIcon from "@mui/icons-material/LightbulbRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import GppMaybeRoundedIcon from "@mui/icons-material/GppMaybeRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import {
	DayHomeStat,
	EmployeeData,
	EmployeeRestControllerApiFactory,
	EmpStatsRestControllerApiFactory,
	EmpValidationRestControllerApiFactory,
	MonthHomeStat,
	PendingValidation,
	PersonReminderList,
} from "arteco-api-client-ts";
import useSWR from "swr";
import dayjs from "dayjs";
import {Link as RouterLink} from "react-router-dom";
import {useApiManager} from "../../components/utils/api";
import {btnSx, FilterChips, Page, Panel, StatTile} from "../../components/kit";
import {useLastEvaluation} from "./employee_util";
import {TimeClockBanner} from "./timeclock/banner";
import {PushAsk} from "./timeclock/push_ask";
import {InstallCard} from "./timeclock/install_card";

const hours = (h?: number) => (h || 0).toLocaleString("es-ES", {minimumFractionDigits: 1, maximumFractionDigits: 1});

/** Portada del portal del empleado: lo que hay que hacer hoy, las horas del mes y los accesos. */
export const EmployeePage = () => {
	const apiManager = useApiManager();
	const [me, setMe] = useState<EmployeeData>();

	useEffect(() => {
		apiManager.executeRaw(apiManager.factory(EmployeeRestControllerApiFactory).viewEmployeeInfo(), undefined, true)
			.then(r => setMe(r.data)).catch(() => undefined);
	}, []);

	const today = dayjs().format("dddd, D [de] MMMM");

	return (
		<Page>
			<Box sx={{mb: 3}}>
				<Typography variant="h5" component="h1" fontWeight={700}>
					{me?.name ? `Hola, ${me.name}` : "Portal del empleado"}
				</Typography>
				<Typography variant="body2" color="text.secondary">{today.charAt(0).toUpperCase() + today.substring(1)}</Typography>
			</Box>

			<Stack spacing={3}>
				<ForceEvaluation/>
				{/* Lo primero: fichar la entrada o la salida (#13709) */}
				<TimeClockBanner/>
				<InstallCard/>
				<PushAsk/>
				<PendingValidations/>
				<QuickLinks/>
				{/* Rejilla CSS y no Grid: dentro de un Stack, el margen negativo de Grid se pierde y desborda. */}
				<Box sx={{display: "grid", gridTemplateColumns: {xs: "minmax(0, 1fr)", lg: "minmax(0, 2fr) minmax(0, 1fr)"}, gap: 3, alignItems: "start"}}>
					<MonthHours/>
					<Reminders/>
				</Box>
			</Stack>
		</Page>
	);
};

/**
 * Validaciones de seguridad asignadas a quien entra (#13988): sus issues abiertas del subproyecto de
 * validaciones, con enlace a Redmine. Sin ninguna, no hay bloque.
 */
const PendingValidations = () => {
	const apiManager = useApiManager();
	const [items, setItems] = useState<PendingValidation[]>([]);

	useEffect(() => {
		apiManager.execute(apiManager.factory(EmpValidationRestControllerApiFactory).getPendingValidations())
			.then(setItems).catch(() => setItems([]));
	}, []);

	if (items.length === 0) {
		return null;
	}
	return (
		<Panel title="Pendiente de resolver" subtitle="Validaciones de seguridad que te han asignado. Se abren en Redmine."
			   icon={<GppMaybeRoundedIcon fontSize="small" color="warning"/>}>
			<Stack divider={<Box sx={{borderTop: "1px solid", borderColor: "divider"}}/>}>
				{items.map(v => (
					<ButtonBase key={v.id} component="a" href={v.url} target="_blank" rel="noreferrer"
								sx={{display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5,
									textAlign: "left", py: 1.25, px: 0.5, borderRadius: 1}}>
						<Box sx={{minWidth: 0}}>
							<Typography variant="body2" fontWeight={600}>#{v.id} · {v.subject}</Typography>
							<Typography variant="caption" color="text.secondary">
								{v.status}{v.updated ? ` · actualizada el ${dayjs(v.updated).format("D/M/YYYY")}` : ""}
							</Typography>
						</Box>
						<OpenInNewRoundedIcon fontSize="small" color="action" sx={{flexShrink: 0}}/>
					</ButtonBase>
				))}
			</Stack>
		</Panel>
	);
};

/** Más de un mes sin autoevaluación: el resto del portal queda bloqueado hasta hacerla. */
const ForceEvaluation = () => {
	const [forceEvaluation] = useLastEvaluation();
	const theme = useTheme();
	if (!forceEvaluation) {
		return null;
	}
	return (
		<Box sx={{
			p: {xs: 2.5, sm: 3}, borderRadius: 3, border: "1px solid", borderColor: alpha(theme.palette.warning.main, 0.4),
			bgcolor: alpha(theme.palette.warning.main, 0.08),
		}}>
			<Stack direction={{xs: "column", sm: "row"}} spacing={2} alignItems={{sm: "center"}} justifyContent="space-between">
				<Stack direction="row" spacing={1.5} alignItems="flex-start">
					<WarningAmberRoundedIcon color="warning"/>
					<Box>
						<Typography variant="subtitle1" fontWeight={700}>Tienes pendiente la autoevaluación del mes</Typography>
						<Typography variant="body2" color="text.secondary">
							Son unos minutos. Hasta que la envíes, el resto del portal queda desactivado.
						</Typography>
					</Box>
				</Stack>
				<Button component={RouterLink} to="/app/employee/evaluation" variant="contained" disableElevation
						sx={{...btnSx, whiteSpace: "nowrap", alignSelf: {xs: "flex-start", sm: "auto"}}}>
					Hacerla ahora
				</Button>
			</Stack>
		</Box>
	);
};

const LINKS: { to: string, label: string, icon: ReactNode }[] = [
	{to: "/app/employee/timeclock", label: "Registro de jornada", icon: <AccessTimeRoundedIcon/>},
	{to: "/app/employee/vacation", label: "Ausencias", icon: <BeachAccessRoundedIcon/>},
	{to: "/app/employee/payroll", label: "Nóminas", icon: <ReceiptLongRoundedIcon/>},
	{to: "/app/employee/evaluation", label: "Autoevaluación", icon: <FactCheckRoundedIcon/>},
	{to: "/app/employee/workbooking", label: "Reservar oficina", icon: <ChairRoundedIcon/>},
	{to: "/app/employee/document", label: "Tus documentos", icon: <DescriptionRoundedIcon/>},
];

const QuickLinks = () => {
	const theme = useTheme();
	return (
		<Box sx={{display: "grid", gridTemplateColumns: {xs: "repeat(3, 1fr)", md: "repeat(6, 1fr)"}, gap: 1.5}}>
			{LINKS.map(l => (
				<ButtonBase key={l.to} component={RouterLink} to={l.to} sx={{
					flexDirection: "column", gap: 1, py: 2, px: 1, borderRadius: 3, border: "1px solid", borderColor: "divider",
					bgcolor: "background.paper", transition: "border-color .15s", "&:hover": {borderColor: "primary.main"},
				}}>
					<Box sx={{
						width: 40, height: 40, borderRadius: 2, display: "grid", placeItems: "center",
						bgcolor: alpha(theme.palette.primary.main, 0.1), color: "primary.main",
					}}>{l.icon}</Box>
					<Typography variant="caption" fontWeight={600} textAlign="center" sx={{lineHeight: 1.2}}>{l.label}</Typography>
				</ButtonBase>
			))}
		</Box>
	);
};

/** Horas previstas frente a las imputadas en Redmine, del mes en curso o del anterior, en calendario. */
const MonthHours = () => {
	const apiManager = useApiManager();
	const statCrl = apiManager.factory(EmpStatsRestControllerApiFactory);
	const {data: stats, isLoading} = useSWR("home.user.stats", () => apiManager.execute(statCrl.getHomeStats()));
	const [which, setWhich] = useState<"actual" | "previous">("actual");

	const month: MonthHomeStat | undefined = stats ? (which === "actual" ? stats.actual : stats.previous) : undefined;
	const planned = month?.totalPlannedHours || 0;
	const imputed = month?.totalRedmineHours || 0;
	const diff = imputed - planned;
	const monthName = (m?: MonthHomeStat) => m?.year ? dayjs(new Date(m.year, (m.month || 1) - 1, 1)).format("MMMM") : "";

	return (
		<Panel title="Horas del mes" subtitle="Lo previsto según tu calendario y lo imputado en Redmine."
			   action={stats?.actual && stats?.previous && (
				   <FilterChips value={which} onChange={setWhich} options={[
					   {value: "actual" as const, label: monthName(stats.actual).replace(/^./, c => c.toUpperCase())},
					   {value: "previous" as const, label: monthName(stats.previous).replace(/^./, c => c.toUpperCase())},
				   ]}/>
			   )}>
			{isLoading || !month ? (
				isLoading ? <Skeleton variant="rounded" height={320}/> :
					<Typography variant="body2" color="text.secondary">No se han podido leer las horas.</Typography>
			) : (
				<>
					<Grid container spacing={1.5} sx={{mb: 2.5}}>
						<Grid item xs={4}><StatTile label="Previstas" value={hours(planned)}/></Grid>
						<Grid item xs={4}><StatTile label="En Redmine" value={hours(imputed)}/></Grid>
						<Grid item xs={4}>
							<StatTile label="Diferencia" tone={Math.abs(diff) < 0.05 ? "success" : "warning"}
									  value={<Box component="span" sx={{color: Math.abs(diff) < 0.05 ? "success.main" : "warning.dark"}}>
										  {diff > 0 ? "+" : ""}{hours(diff)}
									  </Box>}/>
						</Grid>
					</Grid>
					<Calendar month={month}/>
					<Stack direction="row" spacing={1} sx={{mt: 2, color: "text.secondary"}}>
						<InfoOutlinedIcon sx={{fontSize: 16, mt: "2px"}}/>
						<Typography variant="caption">
							Leídas de Redmine el {dayjs(month.tstamp).format("D/M [a las] HH:mm")}; se refrescan cada {stats?.cachedHours} horas.
							Si imputas en el Jira del cliente, las del mes en curso aparecen a cero hasta que se sube el fichero al cerrar el mes.
						</Typography>
					</Stack>
				</>
			)}
		</Panel>
	);
};

const WEEKDAYS = ["L", "M", "X", "J", "V", "S", "D"];

/** Un mes en rejilla de lunes a domingo; cada día con lo imputado y lo previsto, en color si no casan. */
const Calendar = ({month}: { month: MonthHomeStat }) => {
	const theme = useTheme();
	const days = month.calendar || [];
	if (days.length === 0) {
		return null;
	}
	const offset = (dayjs(days[0].date).day() + 6) % 7;
	const today = dayjs().format("YYYY-MM-DD");

	const cellColor = (d: DayHomeStat) => {
		const planned = d.plannedHours || 0;
		const imputed = d.redmineHours || 0;
		if (d.vacationName || d.holidayName) {
			return {bg: alpha(theme.palette.info.main, 0.08), fg: theme.palette.info.dark};
		}
		if (planned < 0.1 && imputed < 0.1) {
			return {bg: "transparent", fg: theme.palette.text.disabled};
		}
		if ((d.date || "") > today) {
			return {bg: theme.palette.grey[50], fg: theme.palette.text.secondary};
		}
		return Math.abs(imputed - planned) < 0.05
			? {bg: alpha(theme.palette.success.main, 0.1), fg: theme.palette.success.dark}
			: {bg: alpha(theme.palette.warning.main, 0.12), fg: theme.palette.warning.dark};
	};

	return (
		<Box>
			<Box sx={{display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: {xs: 0.5, sm: 0.75}}}>
				{WEEKDAYS.map(w => (
					<Typography key={w} variant="caption" color="text.secondary" textAlign="center" fontWeight={600}>{w}</Typography>
				))}
				{Array.from({length: offset}).map((_, i) => <Box key={"e" + i}/>)}
				{days.map(d => {
					const c = cellColor(d);
					const absence = d.vacationName || d.holidayName;
					const isToday = d.date === today;
					return (
						<Tooltip key={d.date} title={`${dayjs(d.date).format("ddd D")}: ${hours(d.redmineHours)} h en Redmine de ${hours(d.plannedHours)} previstas${absence ? " · " + absence : ""}`}>
							<Box sx={{
								borderRadius: 1.5, p: {xs: 0.5, sm: 0.75}, minHeight: {xs: 44, sm: 56}, bgcolor: c.bg,
								border: "1px solid", borderColor: isToday ? "primary.main" : "transparent",
								display: "flex", flexDirection: "column", justifyContent: "space-between",
							}}>
								<Typography variant="caption" sx={{fontWeight: isToday ? 700 : 500, color: "text.secondary", lineHeight: 1}}>
									{dayjs(d.date).date()}
								</Typography>
								{absence ? (
									<Typography variant="caption" noWrap sx={{color: c.fg, fontSize: 10, display: {xs: "none", sm: "block"}}}>{absence}</Typography>
								) : (d.date || "") > today ? (
									// Lo que queda de mes: solo lo previsto, en gris, para no leerlo como horas que faltan.
									d.plannedHours ? <Typography variant="caption" textAlign="right" color="text.disabled" sx={{fontVariantNumeric: "tabular-nums"}}>
										{hours(d.plannedHours)}
									</Typography> : null
								) : (d.plannedHours || d.redmineHours) ? (
									<Typography variant="body2" fontWeight={700} textAlign="right" sx={{color: c.fg, fontVariantNumeric: "tabular-nums", fontSize: {xs: 11, sm: 14}}}>
										{hours(d.redmineHours)}
									</Typography>
								) : null}
							</Box>
						</Tooltip>
					);
				})}
			</Box>
			<Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{mt: 1.5}}>
				{[
					{c: alpha(theme.palette.success.main, 0.25), l: "Cuadra con lo previsto"},
					{c: alpha(theme.palette.warning.main, 0.3), l: "No cuadra"},
					{c: alpha(theme.palette.info.main, 0.2), l: "Ausencia o festivo"},
				].map(x => (
					<Stack key={x.l} direction="row" spacing={0.75} alignItems="center">
						<Box sx={{width: 10, height: 10, borderRadius: 0.5, bgcolor: x.c}}/>
						<Typography variant="caption" color="text.secondary">{x.l}</Typography>
					</Stack>
				))}
			</Stack>
		</Box>
	);
};

/** Las normas de convivencia, una cada vez, sin tapar la pantalla. */
const Reminders = () => {
	const apiManager = useApiManager();
	const [reminders, setReminders] = useState<PersonReminderList[]>();
	const [idx, setIdx] = useState(0);

	useEffect(() => {
		apiManager.execute(apiManager.factory(EmployeeRestControllerApiFactory).getEmployeeReminders())
			.then(list => {
				const shuffled = [...(list || [])];
				for (let i = shuffled.length - 1; i > 0; i--) {
					const j = Math.floor(Math.random() * (i + 1));
					[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
				}
				setReminders(shuffled);
			})
			.catch(() => setReminders([]));
	}, []);

	if (reminders && reminders.length === 0) {
		return null;
	}
	return (
		<Panel title="Recuerda que…" icon={<LightbulbRoundedIcon fontSize="small" sx={{color: "warning.main"}}/>}
			   action={reminders && reminders.length > 1 && (
				   <Typography variant="caption" color="text.secondary">{idx + 1} de {reminders.length}</Typography>
			   )}>
			{!reminders ? <Skeleton variant="rounded" height={100}/> : (
				<>
					<Typography variant="body2" sx={{minHeight: 96}}>{reminders[idx].content}</Typography>
					{reminders.length > 1 && (
						<Button size="small" onClick={() => setIdx((idx + 1) % reminders.length)} sx={{...btnSx, mt: 1, ml: -1}}>
							Ver otro
						</Button>
					)}
				</>
			)}
		</Panel>
	);
};
